'use client';

import Image from "next/image";
import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { montserratMedium, montserratExtraBold, montserratSemi } from "../fonts";
import HomeSectionTitle from './home-section-title';
import GloryBorder from './glory-border';
import RoadmapMobile from './roadmap-mobile';

export default function RoadmapSection() {
  const t = useTranslations('home.roadmap');
  const tItems = useTranslations('home.roadmap.items');

  const roadmapItems = [
    {
      id: 1,
      period: tItems('platformLaunch.period'),
      title: tItems('platformLaunch.title'),
      description: tItems('platformLaunch.description'),
      position: 'left' as const,
    },
    {
      id: 2,
      period: tItems('strategyXVaults.period'),
      title: tItems('strategyXVaults.title'),
      description: tItems('strategyXVaults.description'),
      position: 'right' as const,
    },
    {
      id: 3,
      period: tItems('predictionMarket.period'),
      title: tItems('predictionMarket.title'),
      description: tItems('predictionMarket.description'),
      position: 'left' as const,
    },
    {
      id: 4,
      period: tItems('tradingCompetitions.period'),
      title: tItems('tradingCompetitions.title'),
      description: tItems('tradingCompetitions.description'),
      position: 'right' as const,
    },
    {
      id: 5,
      period: tItems('memeLayerActivation.period'),
      title: tItems('memeLayerActivation.title'),
      description: tItems('memeLayerActivation.description'),
      position: 'left' as const,
    },
    {
      id: 6,
      period: tItems('creatorCoinLaunchpad.period'),
      title: tItems('creatorCoinLaunchpad.title'),
      description: tItems('creatorCoinLaunchpad.description'),
      position: 'right' as const,
    },
    {
      id: 7,
      period: tItems('tge.period'),
      title: tItems('tge.title'),
      description: tItems('tge.description'),
      position: 'left' as const,
    },
  ];

  return (
    <section
      id="told-section"
      className="section-container-full-height"
    >
      <HomeSectionTitle
        title={t('title')}
        description={t('description')}
        centered={false}
        containerClassName="text-left"
      />

      {/* Desktop view */}
      <div className="hidden lg:block w-full max-w-5xl">
        <div className="relative">
          {/* Timeline line - Desktop center */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-gradient-to-b from-[color:var(--main-color)] via-[color:var(--main-color)] to-transparent opacity-30"></div>
          <div className="absolute top-[-19%] left-[-25vw] z-10 pointer-events-none">
            <Image
              src="/assets/radio-1.svg"
              alt={t('altText')}
              width={632}
              height={345}
              loading="lazy"
            />
          </div>
          <div className="absolute top-[-37%] right-[-25vw] z-10 pointer-events-none">
            <Image
              src="/assets/radio-2.svg"
              alt={t('altText')}
              width={632}
              height={345}
              loading="lazy"
            />
          </div>
          {/* Roadmap Items */}
          <div className="space-y-[-50px]">
            {roadmapItems.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ 
                  opacity: 0, 
                  x: item.position === 'left' ? -80 : 80 
                }}
                whileInView={{ 
                  opacity: 1, 
                  x: 0 
                }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ 
                  duration: 0.8, 
                  delay: index * 0.1,
                  ease: [0.16, 1, 0.3, 1] 
                }}
                className="relative flex flex-col md:flex-row items-start md:items-center gap-8"
                style={{ willChange: 'transform, opacity' }}
              >
                {item.position === 'left' ? (
                  <>
                    <div className="md:w-1/2 md:text-right flex flex-col items-start md:items-end w-full">
                      <div className="mr-[40px] relative">
                        <div
                          className="w-4 h-4 rounded-full border-4 border-[color:var(--background)] absolute z-10"
                          style={{
                            background: 'linear-gradient(180deg, #FFFFFF, #8B9CB6, #2F4D7D)',
                            top: '50%',
                            right: '-48px',
                            transform: 'translateY(-50%)',
                          }}
                        />
                          <div className="bg-[#030303] lg:w-[450px] xl:w-[500px] md:pr-8 w-full border-1 border-[var(--field-border)] rounded-2xl min-h-[216px] justify-center flex flex-col overflow-hidden relative group">
                            <div className={`${montserratSemi.className} text-base text-[color:var(--main-color)] mb-2`}>
                              {item.period}
                            </div>
                            <h3 
                              className={`${montserratExtraBold.className} text-lg md:text-2xl text-white mb-2`}
                              dangerouslySetInnerHTML={{ __html: item.title }}
                            />
                            <p className={`${montserratMedium.className} text-sm text-[color:var(--text-muted)] leading-relaxed pl-4 xl:pl-6`}>
                              {item.description}
                            </p>
                            <div className="absolute bottom-0 left-0 translate-x-[-50%] translate-y-[50%]">
                              <Image
                                src="/assets/roadmap-radio.svg"
                                alt={t('roadmapRadio')}
                                width={317}
                                height={295}
                                className="w-full h-full object-contain"
                                loading="lazy"
                              />
                              <div
                                className="absolute top-1/2 left-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                                style={{
                                  filter: 'blur(70px)',
                                  background: 'var(--main-color)',
                                  width: '163px',
                                  height: '150px',
                                  transform: 'translate(-50%, -50%)',
                                  borderRadius: '50%',
                                }}
                              />
                            </div>
                          </div>
                      </div>
                    </div>
                    <div className="md:w-1/2 hidden md:block"></div>
                  </>
                ) : (
                  <>
                    <div className="md:w-1/2 hidden md:block"></div>
                    <div className="md:w-1/2 md:text-left flex flex-col items-start w-full">
                      <div className="ml-[40px] relative">
                        <div
                          className="w-4 h-4 rounded-full border-4 border-[color:var(--background)] absolute z-10"
                          style={{
                            background: 'linear-gradient(180deg, #FFFFFF, #8B9CB6, #2F4D7D)',
                            top: '50%',
                            left: '-48px',
                            transform: 'translateY(-50%)',
                          }}
                        />
                          <div className={`bg-[#030303] lg:w-[450px] xl:w-[500px] md:pl-8 w-full ${item.id === 5 ? 'border-2' : 'border-1'} border-[var(--field-border)] rounded-2xl min-h-[216px] justify-center flex flex-col overflow-hidden relative group`}>
                            <div className={`${montserratSemi.className} text-base text-[color:var(--main-color)] mb-2`}>
                              {item.period}
                            </div>
                            <h3 
                              className={`${montserratExtraBold.className} text-lg md:text-2xl text-white mb-2`}
                              dangerouslySetInnerHTML={{ __html: item.title }}
                            />
                            <p className={`${montserratMedium.className} text-sm text-[color:var(--text-muted)] leading-relaxed pr-4 xl:pr-6`}>
                              {item.description}
                            </p>
                            <div className="absolute bottom-0 right-0 translate-x-[50%] translate-y-[50%]">
                              <Image
                                src="/assets/roadmap-radio.svg"
                                alt={t('roadmapRadio')}
                                width={317}
                                height={295}
                                className="w-full h-full object-contain"
                                loading="lazy"
                              />
                              <div
                                className="absolute top-1/2 left-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                                style={{
                                  filter: 'blur(70px)',
                                  background: 'var(--main-color)',
                                  width: '163px',
                                  height: '150px',
                                  transform: 'translate(-50%, -50%)',
                                  borderRadius: '50%',
                                }}
                              />
                            </div>
                          </div>
                      </div>
                    </div>
                  </>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile view */}
      <div className="block lg:hidden w-full">
        <RoadmapMobile items={roadmapItems} />
      </div>
    </section>
  );
}

